import type { KpiComparison } from "@types";
export type FuelType = "Diesel" | "Elétrico" | "GNV" | "Biodiesel";

export interface FuelingRecord {
  id: string;
  date: string;
  driver: string;
  driverStatus: "connected" | "team";
  driverPhoto?: string;
  vehicle: string;
  location: string;
  fuelType: FuelType;
  quantity: string;
  quantityUnit: string;
  amount: string;
}

const fuelingMeasures: Record<FuelType, { quantity: string; quantityUnit: string }> = {
  Diesel: { quantity: "41,6", quantityUnit: "L" },
  Biodiesel: { quantity: "41,6", quantityUnit: "L" },
  Elétrico: { quantity: "166", quantityUnit: "kWh" },
  GNV: { quantity: "55", quantityUnit: "Kg" },
};

const fuelingDriverStatus: Record<string, FuelingRecord["driverStatus"]> = {
  "Ricardo de Marchi": "team",
  "Leandro Bertini Neto": "connected",
  "Marcos Pereira Filho": "team",
  "Lourival Aparecido": "team",
};

type FuelingRow = [date: string, driver: string, location: string, fuelType: FuelType];

const fuelingRows: FuelingRow[] = [
  ["10/10/2024 - 12:11", "Ricardo de Marchi", "Rua das Flores, 128 – Curitiba, PR", "Diesel"],
  ["09/10/2024 - 01:00", "Leandro Bertini Neto", "Av. Paulista, 742 – São Paulo, SP", "Diesel"],
  ["08/10/2024 - 09:00", "Marcos Pereira Filho", "Rua XV de Novembro, 55 - Blumenau, SC", "Elétrico"],
  ["07/10/2024 - 12:00", "Lourival Aparecido", "Av. Brasil, 980 – Uberlândia, MG", "Diesel"],
  ["06/10/2024 - 13:00", "Ricardo de Marchi", "Rua Goiás, 312 – Goiânia, GO", "Elétrico"],
  ["05/10/2024 - 14:00", "Leandro Bertini Neto", "Av. Atlântica, 1040 – Rio de Janeiro, RJ", "Elétrico"],
  ["04/10/2024 - 13:00", "Lourival Aparecido", "Av. Atlântica, 1040 – Rio de Janeiro, RJ", "Elétrico"],
  ["03/10/2024 - 11:00", "Lourival Aparecido", "Rua Santos Dumont, 67 – Londrina, PR", "GNV"],
  ["02/10/2024 - 13:00", "Leandro Bertini Neto", "Av. Paulista, 742 – São Paulo, SP", "Biodiesel"],
  ["01/10/2024 - 12:00", "Leandro Bertini Neto", "Av. Paulista, 742 – São Paulo, SP", "Biodiesel"],
];

export const fuelingMock: FuelingRecord[] = fuelingRows.map(([date, driver, location, fuelType], position) => ({
  id: String(position + 1),
  date,
  driver,
  driverStatus: fuelingDriverStatus[driver],
  vehicle: "Constellation 14.210",
  location,
  fuelType,
  ...fuelingMeasures[fuelType],
  amount: "R$ 250,00",
}));

export interface FuelTypeDetail {
  quantity: number;
  expense: number;
}

export interface TopVehicle {
  vehiclePlate: string;
  driverName: string;
  fuelType: string;
  consumptionUnit: string;
  consumptionValue: number;
  consumptionCurrency: number;
}

export interface EfficiencyVehicle {
  vehicle: {
    id: string;
    name: string;
  };
  drivers: Array<{
    id: string;
    name: string;
  }>;
  fuelType: string;
  fuelVolume: number;
  fuelUnit: string;
  fuelTotalPrice: number;
}

export interface EfficiencyResponse {
  mostEconomicVehicles: EfficiencyVehicle[];
  leastEconomicVehicles: EfficiencyVehicle[];
}

export interface FuelingSummary {
  totalExpenseKpi?: KpiComparison | null;
  arla32ExpenseKpi?: KpiComparison | null;
  fuelExpenseKpi?: KpiComparison | null;
  averageFleetConsumptionKpi?: KpiComparison | null;
  quantityDiesel: number;
  quantityElectric: number;
  quantityGNV: number;
  arla32Volume: number;
  totalFuelings: number;
  fuelExpense: number;
  fuelExpenseByType: {
    diesel: { value: number; percentage: number };
    electric: { value: number; percentage: number };
    gnv: { value: number; percentage: number };
  };
  fuelTypes: {
    biodiesel?: FuelTypeDetail;
    diesel?: FuelTypeDetail;
    etanol?: FuelTypeDetail;
    gasolina?: FuelTypeDetail;
    eletrico?: FuelTypeDetail;
    gnv?: FuelTypeDetail;
  };
  arla32Expense: number;
  totalExpense: number;
  totalExpenseVsPreviousMonthPercentage: number;
  fuelTotalPriceVsPreviousMonthPercentage: number;
  complementValueARLATotalVsPreviousMonthPercentage: number;
  fleetVehicleTotal: number;
  supplyTotal: number;
  fleetTotal: number;
  averageFleetConsumption: number;
  topVehiclesMostEconomical?: TopVehicle[];
  topVehiclesLeastEconomical?: TopVehicle[];
}

export const fuelingSummaryMock: FuelingSummary = {
  quantityDiesel: 2523.53,
  quantityElectric: 950.4,
  quantityGNV: 1188.8,
  arla32Volume: 311,
  totalFuelings: 18,
  fuelExpense: 22639.66,
  fuelExpenseByType: {
    diesel: { value: 17820.0, percentage: 75 },
    electric: { value: 2376.0, percentage: 10 },
    gnv: { value: 5940.0, percentage: 25 },
  },
  fuelTypes: {
    biodiesel: { quantity: 482.84, expense: 3822.0 },
    diesel: { quantity: 2040.0, expense: 15306.2 },
    etanol: { quantity: 0, expense: 0 },
    gasolina: { quantity: 0, expense: 2386.28 },
    eletrico: { quantity: 120.0, expense: 1142.6 },
    gnv: { quantity: 28, expense: 1982.58 },
  },
  arla32Expense: 1386.0,
  totalExpense: 24948.0,
  totalExpenseVsPreviousMonthPercentage: 10,
  fuelTotalPriceVsPreviousMonthPercentage: 10,
  complementValueARLATotalVsPreviousMonthPercentage: 10,
  fleetVehicleTotal: 22,
  supplyTotal: 18,
  fleetTotal: 22,
  averageFleetConsumption: 1257.76,
  topVehiclesMostEconomical: [
    {
      vehiclePlate: "e-Delivery 11",
      driverName: "Ricardo Silva",
      fuelType: "Elétrico",
      consumptionUnit: "kWh",
      consumptionValue: 58.0,
      consumptionCurrency: 551.59,
    },
    {
      vehiclePlate: "Constellation 14.210",
      driverName: "João Alves",
      fuelType: "GNV",
      consumptionUnit: "Kg",
      consumptionValue: 28,
      consumptionCurrency: 560.0,
    },
    {
      vehiclePlate: "e-Delivery 14",
      driverName: "Renato Freitas",
      fuelType: "Elétrico",
      consumptionUnit: "kWh",
      consumptionValue: 62.0,
      consumptionCurrency: 591.01,
    },
    {
      vehiclePlate: "Constellation 24.280",
      driverName: "João Marques",
      fuelType: "Biodiesel",
      consumptionUnit: "L",
      consumptionValue: 35.0,
      consumptionCurrency: 665.0,
    },
    {
      vehiclePlate: "Constellation 20.480",
      driverName: "Antônio Peres",
      fuelType: "Diesel",
      consumptionUnit: "L",
      consumptionValue: 40.0,
      consumptionCurrency: 72.0,
    },
  ],
  topVehiclesLeastEconomical: [
    {
      vehiclePlate: "Constellation 31.390",
      driverName: "Carlos Santos",
      fuelType: "Diesel",
      consumptionUnit: "L",
      consumptionValue: 120.0,
      consumptionCurrency: 1800.0,
    },
    {
      vehiclePlate: "Constellation 25.460",
      driverName: "Pedro Lima",
      fuelType: "Diesel",
      consumptionUnit: "L",
      consumptionValue: 115.0,
      consumptionCurrency: 1725.0,
    },
    {
      vehiclePlate: "Constellation 19.390",
      driverName: "Marcos Silva",
      fuelType: "Diesel",
      consumptionUnit: "L",
      consumptionValue: 110.0,
      consumptionCurrency: 1650.0,
    },
    {
      vehiclePlate: "e-Delivery 9",
      driverName: "Lucas Costa",
      fuelType: "Elétrico",
      consumptionUnit: "kWh",
      consumptionValue: 95.0,
      consumptionCurrency: 950.0,
    },
    {
      vehiclePlate: "Constellation 17.280",
      driverName: "Fernando Ramos",
      fuelType: "Biodiesel",
      consumptionUnit: "L",
      consumptionValue: 85.0,
      consumptionCurrency: 1275.0,
    },
  ],
};
